<template>
  <div>
    <div v-if="showIntro" class="row">
      <div
        class="col-md-7 mx-auto bg-white rounded-3 shadow-sm p-5 border border-light"
      >
        <p class="text-center">
          <i class="bi bi-shield-check display-2 mb-5"></i>
        </p>
        <h2 class="skin-page-heading text-center">Guaranteed Income</h2>
        <p class="lead mt-4">
          Buying some lifetime guaranteed income when you retire, or if you are
          in retirement can help in many ways. It will reduce the risk of not
          achieving what you want in retirement and will be payable no matter
          how long you live. This ensures you have the certainty of a guaranteed
          stream of monthly income.
        </p>
        <p>
          Some forms of lifetime guaranteed income now have surrender values in
          the first few years, in case your health or financial needs change
          dramatically. For all guaranteed income products you can usually get
          better rates if you are willing to answer some questions regarding
          your health and lifestyle.
        </p>
        <p>
          Answer one question and we can see which form of guaranteed income
          seems best for you.
        </p>

        <p>
          <button
            type="button"
            class="btn btn-primary mt-3"
            @click="onStartClick"
          >
            Start
          </button>
        </p>
      </div>
    </div>

    <div v-if="showQuestions" class="row">
      <div class="col-md-6 mx-auto header">
        <h2 class="skin-page-heading">Guaranteed Income</h2>

        <p class="question text-start mt-5">
          Which option below best describes how you want to add some guaranteed
          income to your plan?
        </p>

        <div class="answers">
          <div
            v-for="(answer, i) in answers"
            v-bind:key="i"
            class="form-check shadow-sm d-flex align-self-center"
          >
            <input
              class="form-check-input d-flex align-self-center"
              type="radio"
              :name="`flexRadioDefault${questionId}`"
              :id="`flexRadioDefault${questionId}${i}`"
              :value="i"
              v-model="questionResponses[questionId]"
            />
            <label
              class="form-check-label"
              :for="`flexRadioDefault${questionId}${i}`"
            >
              {{ answer }}
            </label>
          </div>
        </div>

        <div class="mt-5">
          <button
            type="button"
            class="btn btn-primary"
            @click="onContinueClick"
          >
            Continue
          </button>
          <div>
            <p class="text-start">
              <a
                href=""
                class="btn btn-outline-dark"
                @click="onBackClick($event)"
              >
                <i class="bi bi-arrow-left"></i>
                Back
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showOutcome" class="row">
      <div class="col-xl-11 mx-auto header">
        <h2 class="skin-page-heading mb-5">
          Want to add some Guaranteed Income to your plans?
        </h2>

        <template v-if="outcome == GuaranteedIncomeOutcome.RetirementLine">
          <p class="mx-auto col-md-9 mb-5 lead">
            {{ retirementLineHeaderText }}
          </p>
          <sub-section title="Partners" :showHelp="false">
            <table class="table table-providers mb-0">
              <thead>
                <tr>
                  <th>Provider</th>
                  <th>Best suited to?</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td class="align-middle">
                    <img
                      src="/Content/images/Providers/logo-retirementline.png"
                      class="img-fluid"
                      alt="Retirement Line"
                    />
                  </td>
                  <td class="text-start align-middle">
                      Retirement Line is completely independent and as the largest annuity broker in the UK will 
                      search the whole market for you to find the best priced annuity specific to your needs and 
                      circumstances.
                  </td>
                  <td class="align-middle">
                    <button
                      type="button"
                      class="btn btn-warning text-nowrap w-100"
                      @click="visitRetirementLineClicked"
                    >
                      Visit Retirement Line
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </sub-section>
        </template>
        <template v-else-if="outcome == GuaranteedIncomeOutcome.JustModelling">
          <guaranteed-income-modeller />
        </template>
        <div v-if="showBackButton">
          <p class="text-start">
            <a
              href=""
              class="btn btn-outline-dark"
              @click="onBackClick($event)"
            >
              <i class="bi bi-arrow-left"></i>
              Back
            </a>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import GuaranteedIncomeModeller from "@/guiide/components/guaranteedIncome/GuaranteedIncomeModeller.vue";
import SubSection from "@/guiide/components/common/SubSection.vue";
import Monitoring from "@/guiide/ts/monitoring/monitoring";
import { guiidePlusMonitoring } from "@/guiide/ts/guiideplus/guiidePlusMonitoring";

enum ActiveSection {
  Intro,
  Question,
  Outcome
}

enum GuaranteedIncomeOutcome {
  NoOutcome,
  RetirementLine,
  JustModelling
}

export default defineComponent({
  name: "GuaranteedIncome",
  components: {
    GuaranteedIncomeModeller,
    SubSection
  },
  data() {
    return {
      activeSection: ActiveSection.Outcome,
      questionId: 0,
      questionResponses: [],
      outcome: GuaranteedIncomeOutcome.JustModelling,
      GuaranteedIncomeOutcome,
      showBackButton: false
    };
  },
  computed: {
    showIntro: function(): boolean {
      return this.activeSection == ActiveSection.Intro;
    },
    showQuestions: function(): boolean {
      return this.activeSection == ActiveSection.Question;
    },
    showOutcome: function(): boolean {
      return this.activeSection == ActiveSection.Outcome;
    },
    answers: function(): Array<string> {
      return [
        "I'd like a mix of both lifetime guaranteed and non-guaranteed flexible income",
        "I want some guaranteed income for just a fixed period (say the next 10 years)",
        "I want to use all my pension pots to buy guaranteed income with no flexible income"
      ];
    },
    selectedAnswer: function(): number {
      if (!this.showOutcome) {
        return -1;
      }
      return this.questionResponses[this.questionId];
    },
    retirementLineHeaderText: function(): string {
      if (this.outcome != GuaranteedIncomeOutcome.RetirementLine) {
        return "";
      }

      if (this.selectedAnswer == 1) {
        return "If you just want to buy some guaranteed income for a fixed period, Retirement Line can help.";
      } else {
        return "If you want to use all your pots to buy guaranteed income, Retirement Line can help.";
      }
    }
  },
  methods: {
    displayModellingOutcome() {
      this.outcome = GuaranteedIncomeOutcome.JustModelling;
      this.activeSection = ActiveSection.Outcome;
    },
    onStartClick() {
      this.activeSection = ActiveSection.Question;
      this.showBackButton = true;
    },
    onContinueClick() {
      if (this.questionResponses.length < 1) {
        return;
      }

      const answerId = this.questionResponses[this.questionId];

      switch (answerId) {
        case 0:
          this.outcome = GuaranteedIncomeOutcome.JustModelling;
          guiidePlusMonitoring.logGuaranteedIncomeModeller();
          break;
        case 1:
          this.outcome = GuaranteedIncomeOutcome.RetirementLine;
          guiidePlusMonitoring.logGuaranteedIncomeFixedPeriod();
          break;
        case 2:
          this.outcome = GuaranteedIncomeOutcome.RetirementLine;
          guiidePlusMonitoring.logGuaranteedIncomeAllIn();
          break;
      }

      this.activeSection = ActiveSection.Outcome;
    },
    onBackClick(event) {
      event.preventDefault();
      if (this.activeSection == ActiveSection.Outcome) {
        this.activeSection = ActiveSection.Question;
      } else {
        this.activeSection = ActiveSection.Intro;
      }
    },
    reset() {
      this.showEditor();
      this.showBackButton = false;
    },
    visitRetirementLineClicked() {
      const potValue = this.$store.state.retiree.pensionPots.presentValue;
      Monitoring.gaEvent("RetirementLine", potValue);
      window.open(
        "https://www.retirementline.co.uk/landing/?v=guiide#yourpension",
        "_blank"
      );
    },
    showEditor() {
      this.activeSection = ActiveSection.Outcome;
      this.outcome = GuaranteedIncomeOutcome.JustModelling;
    }
  }
});
</script>

<style scoped>
.header {
  text-align: center;
}

.h2 {
  display: inline-block;
  padding: 0 30px 15px;
  border-bottom: 1px solid #ccc;
}

.question {
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 0px;
}

.question-conjunction {
  font-size: 20px;
  font-weight: 500;
  font-style: italic;
  margin-top: 15px;
  margin-bottom: 15px;
}

.answers {
  margin: 35px 0;
}

.answers .form-check {
  background-color: #fff;
  padding-left: 0;
  text-align: left;
  border-radius: 8px;
  margin: 16px 0;
  border: 1px solid transparent;
}

.answers .form-check:hover {
  border: 1px solid #00497f;
}

.answers .form-check-input {
  margin: 20px;
  padding: 10px;
}

.answers label {
  display: block;
  padding: 20px;
  cursor: pointer;
}

.table-providers td,
.table-providers .btn-warning {
  font-size: 14px !important;
}

.table-providers > :not(caption) > * > * {
  padding-left: 15px;
  padding-right: 15px;
}

.table-providers td .img-fluid {
  width: 300px;
}

.card-slider p {
  font-size: 14px !important;
}

.btn-primary {
  padding: 10px 45px;
  border-radius: 45px;
  background-color: #3e566e !important;
  border-color: #3e566e !important;
  color: #fff;
}
</style>
